import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, ListGroup } from 'react-bootstrap';

const MAX_VISIBLE = 40;
const MIN_CHARS_LARGE_LIST = 2;
const LARGE_LIST_THRESHOLD = 30;

/**
 * Select con ricerca integrata nel pannello (combobox).
 * options: { value, label, description?, data? }
 */
const SearchableSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = 'Seleziona...',
  emptyLabel = 'Nessuna selezione',
  allowClear = true,
  disabled = false,
  size = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((o) => String(o.value) === String(value)) ?? null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const isLargeList = options.length > LARGE_LIST_THRESHOLD;

    if (isLargeList && term.length < MIN_CHARS_LARGE_LIST) {
      return [];
    }

    let list = options;
    if (term) {
      list = options.filter((o) => {
        const hay = `${o.label ?? ''} ${o.description ?? ''}`.toLowerCase();
        return hay.includes(term);
      });
    }
    return list.slice(0, MAX_VISIBLE);
  }, [options, searchTerm]);

  const showTypeToSearchHint =
    options.length > LARGE_LIST_THRESHOLD && searchTerm.trim().length < MIN_CHARS_LARGE_LIST;

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
    if (isOpen) setSearchTerm('');
  };

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
    setSearchTerm('');
  };

  const controlClass = size === 'sm' ? 'form-select form-select-sm' : 'form-select';

  return (
    <div className="searchable-select position-relative" ref={containerRef}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={`${controlClass} searchable-select-control d-flex align-items-center ${disabled ? 'disabled bg-light' : ''}`}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`flex-grow-1 text-truncate ${!selectedOption ? 'text-muted' : ''}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="d-flex align-items-center ms-2 flex-shrink-0">
          {allowClear && selectedOption && !disabled && (
            <i
              className="bi bi-x-lg me-2 text-muted"
              role="button"
              aria-label="Cancella selezione"
              onClick={handleClear}
            />
          )}
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-muted`} />
        </span>
      </div>

      {isOpen && !disabled && (
        <div
          className="searchable-select-menu border rounded bg-white shadow-sm mt-1 position-absolute w-100"
          style={{ zIndex: 1060 }}
        >
          <div className="p-2 border-bottom">
            <FormControl
              ref={searchInputRef}
              size="sm"
              type="search"
              placeholder="Cerca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>

          <ListGroup variant="flush" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {allowClear && (
              <ListGroup.Item
                action
                className="py-2 text-muted"
                onClick={() => handleSelect(null)}
              >
                {emptyLabel}
              </ListGroup.Item>
            )}

            {showTypeToSearchHint && (
              <ListGroup.Item disabled className="py-2 small text-muted border-0">
                Digita almeno {MIN_CHARS_LARGE_LIST} caratteri per cercare tra {options.length} elementi.
              </ListGroup.Item>
            )}

            {!showTypeToSearchHint && filteredOptions.length === 0 && (
              <ListGroup.Item disabled className="py-2 small text-muted border-0">
                Nessun risultato.
              </ListGroup.Item>
            )}

            {filteredOptions.map((option) => (
              <ListGroup.Item
                key={option.value}
                action
                active={String(option.value) === String(value)}
                className="py-2"
                onClick={() => handleSelect(option)}
              >
                <div className="fw-semibold">{option.label}</div>
                {option.description && (
                  <div className="small text-muted">{option.description}</div>
                )}
              </ListGroup.Item>
            ))}

            {!showTypeToSearchHint && filteredOptions.length >= MAX_VISIBLE && (
              <ListGroup.Item disabled className="py-1 small text-muted border-0">
                Mostrati i primi {MAX_VISIBLE} risultati — affina la ricerca.
              </ListGroup.Item>
            )}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
